import React, { useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import GetPieceConfig from '../../utils/GetPieceConfig';
import temVirgula from '../../utils/temVirgula';
import closeX from '../../img/closeX.svg';

// Tipos de peça
const ItemTypes = {
    PIECE: 'piece',
  };

// Componente que representa as peças na matriz
export default function BoardCell({ 
    type, row, col, cell, 
    onDropPiece, onDeletePiece, onCellClick, onEditLabel, 
    activeContextMenu, onOpenContextMenu, onCloseContextMenu }) {
  
    const [{ isOver }, drop] = useDrop(() => ({
      accept: ItemTypes.PIECE,
      drop: (item) => onDropPiece(row, col, item),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }));
  
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
    const [isEditingLabel, setIsEditingLabel] = useState(false); // Estado para controle de edição
    const [editedLabel, setEditedLabel] = useState(cell?.content?.cellInfo?.label || ""); // Texto do rótulo
    const [showLabel, setShowLabel] = useState(false); // Estado para controlar exibição do rótulo
  
    const handleRightClick = (e) => {
      e.preventDefault();
  
      // Fecha outros menus antes de abrir o novo
      onOpenContextMenu(row, col);
    
      // Obter as coordenadas relativas ao elemento
      const cellRect = e.currentTarget.getBoundingClientRect();
      setContextMenu({
        visible: true,
        x: e.clientX - cellRect.left, // Posição relativa horizontal
        y: e.clientY - cellRect.top,  // Posição relativa vertical
      });
    };
  
    useEffect(() => {
      // Fecha o menu de contexto se a célula não for a ativa
      if (!activeContextMenu || activeContextMenu.row !== row || activeContextMenu.col !== col) {
        setContextMenu({ visible: false, x: 0, y: 0 });
      }
    }, [activeContextMenu]);
  
    const handleCloseContextMenu = () => {
      setContextMenu({ visible: false, x: 0, y: 0 });
      onCloseContextMenu();
    };
  
    const handleRemoveObject = () => {
      onDeletePiece(row, col);
      handleCloseContextMenu();
    };
  
    const handleEditLabelClick = () => {
      setIsEditingLabel(true);
      handleCloseContextMenu();
    };
  
    const handleSaveEdit = () => {
      onEditLabel(row, col, editedLabel); // Atualiza o rótulo usando a função passada por props
      setIsEditingLabel(false);
    };
  
    const handleMouseEnter = () => {
      setShowLabel(true);
    };
  
    const handleMouseLeave = () => {
      setShowLabel(false);
    };
  
    if (cell && cell.content) {
      const pieceConfig = GetPieceConfig(cell.content.size);
      let contentStyle = {};
  
      if (cell.content.type == "rack") {
        if (temVirgula(cell.content.cellInfo?.label)) {
          contentStyle = {
            width: pieceConfig.width,
            height: pieceConfig.height,
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10,
            background: cell.content.cellInfo.status2
              ? "linear-gradient(45deg, #E89F1750 50%, #71CACD 50%)"
              : cell.content.cellInfo.status1
              ? "linear-gradient(45deg, #E9C4C4 70%, #71CACD 30%)"
              : "linear-gradient(45deg, #CDE0CE 50%, #71CACD 50%)",
            display: "flex",
            alignItems: "center",
            border: pieceConfig.border,
            justifyContent: "center",
          };
        } else {
          contentStyle = {
            width: pieceConfig.width,
            height: pieceConfig.height,
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10,
            backgroundColor: cell.content.cellInfo.status2
              ? "#E89F1750"
              : cell.content.cellInfo.status1
              ? "#E9C4C4"
              : "#CDE0CE",
            display: "flex",
            alignItems: "center",
            border: pieceConfig.border,
            justifyContent: "center",
          };
        }
      } else {
        contentStyle = {
          width: pieceConfig.width,
          height: pieceConfig.height,
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 10,
          backgroundColor: pieceConfig.backgroundColor || pieceConfig.color || "#fff0",
          display: "flex",
          alignItems: "center",
          border: pieceConfig.border,
          justifyContent: "center",
        };
      }
  
      return (
        <div
          ref={drop}
          className="board-cell"
          style={{ backgroundColor: isOver ? "lightgreen" : "white", position: "relative" }}
          onContextMenu={handleRightClick} // Capture right-click
          onClick={() => onCellClick(cell)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="piece" style={contentStyle}>
            {pieceConfig.img ? (
              <div className="piece-img">
                <img
                  src={pieceConfig.img}
                  alt={pieceConfig.name}
                  style={cell?.content?.type?.split("-")[0] == "door" ? {objectFit: "contain", width:"100%", height:"100%"} : {objectFit: "contain"}}
                />
                <p>
                {
                  cell.content.type == "rack" ||
                  cell.content.type == "fancoil" ||
                  cell.content.type == "equipment" ||
                  cell.content.type == "rack-mda" ||
                  cell.content.type == "sdu" ||
                  cell.content.type == "empty-floor" ?
                    temVirgula(cell.content.cellInfo?.label)
                      ? cell.content.cellInfo?.label.split("/")[0]
                      : cell.content.cellInfo?.label
                  : <></>
                }
              </p>
              </div>
            ) : (
              <p>
                {
                  cell.content.type == "rack" ||
                  cell.content.type == "fancoil" ||
                  cell.content.type == "equipment" ||
                  cell.content.type == "rack-mda" ||
                  cell.content.type == "sdu" ||
                  cell.content.type == "empty-floor" ?
                    temVirgula(cell.content.cellInfo?.label)
                      ? cell.content.cellInfo?.label.split("/")[0]
                      : cell.content.cellInfo?.label
                  : <></>
                }
              </p>
            )}
          </div>
  
          {/* CARD CELL OPTIONS */}
          {contextMenu.visible && type != "View Only" && cell.content && (
            <div
              className="context-menu"
              style={{
                position: "absolute",
                top: contextMenu.y,
                left: contextMenu.x,
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                zIndex: 20,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img onClick={() => handleCloseContextMenu()} src={closeX}/>
              <button onClick={handleRemoveObject}>Remover objeto</button>
              {
                cell.content.type != "rack" ?
                <button onClick={handleEditLabelClick}>Edit Label</button> : null
              }
            </div>
          )}
  
          {/* Card para edição do rótulo */}
          {isEditingLabel && (
            <div className="edit-label-card">
              <input
                type="text"
                value={editedLabel}
                onChange={(e) => {
                  if (e.target.value.length <= 8) {
                    setEditedLabel(e.target.value);
                  }
                }}
              />
              <button onClick={handleSaveEdit}><p>Save Edit</p></button>
              <button onClick={() => setIsEditingLabel(false)}><p>Cancel</p></button>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div
          ref={drop}
          className="board-cell"
          style={{ backgroundColor: isOver ? "lightgreen" : "white", position: "relative" }}
          onContextMenu={handleRightClick} // Capture right-click
        />
      );
    }
  };